const DEFAULT_RESULT_DIRECTION = 'higher';

export const resultDirectionOptions = [
  { value: 'higher', label: 'Nagyobb érték a jobb' },
  { value: 'lower', label: 'Kisebb érték a jobb' },
];

export const resultUnitOptions = ['db', 'mp', 'perc és mp', 'méter', 'kör'];
export const exerciseUnitOptions = ['mp', 'db', 'méter', 'kör', 'perc'];

export const getTimestampMillis = (value) => {
  if (value?.toMillis) {
    return value.toMillis();
  }
  if (value?.toDate) {
    return value.toDate().getTime();
  }
  if (value instanceof Date) {
    return value.getTime();
  }
  return 0;
};

const normalizeResultDirection = (value) => (value === 'lower' ? 'lower' : DEFAULT_RESULT_DIRECTION);

export const getResultSortValue = (value, direction = DEFAULT_RESULT_DIRECTION) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 0;
  }
  return normalizeResultDirection(direction) === 'lower' ? numericValue : -numericValue;
};

const isBetterResult = (candidate, current, direction = DEFAULT_RESULT_DIRECTION) => {
  const candidateValue = Number(candidate?.resultValue ?? candidate?.result_value);
  const currentValue = Number(current?.resultValue ?? current?.result_value);

  if (!Number.isFinite(candidateValue)) {
    return false;
  }
  if (!Number.isFinite(currentValue)) {
    return true;
  }

  if (candidateValue !== currentValue) {
    return normalizeResultDirection(direction) === 'lower'
      ? candidateValue < currentValue
      : candidateValue > currentValue;
  }

  const candidateTime = getTimestampMillis(candidate?.recordedAt ?? candidate?.recorded_at ?? candidate?.createdAt);
  const currentTime = getTimestampMillis(current?.recordedAt ?? current?.recorded_at ?? current?.createdAt);
  if (!candidateTime) {
    return false;
  }
  if (!currentTime) {
    return true;
  }
  return candidateTime < currentTime;
};

export const formatResultValue = (value, unit = '') => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return '-';
  }

  if (unit === 'perc és mp') {
    const totalSeconds = Math.round(numericValue);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')} perc`;
  }

  const hasFraction = Math.abs(numericValue - Math.round(numericValue)) > 0.001;
  const formatted = new Intl.NumberFormat('hu-HU', {
    maximumFractionDigits: hasFraction ? 2 : 0,
  }).format(numericValue);

  return unit ? `${formatted} ${unit}` : formatted;
};

export const getExerciseName = (exercisesById, exerciseId, fallback = '') => {
  return exercisesById[exerciseId]?.name || fallback || 'Ismeretlen gyakorlat';
};

const formatExerciseDuration = (exercise = {}) => {
  const minutes = Number(exercise.durationMinutes || 0);
  const seconds = Number(exercise.durationSeconds || 0);
  if (!minutes && !seconds) {
    return '';
  }
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

export const formatExerciseTask = (exercise = {}) => {
  const parts = [];
  const repetitionCount = Number(exercise.repetitionCount);
  if (Number.isFinite(repetitionCount) && repetitionCount > 0) {
    parts.push(`${new Intl.NumberFormat('hu-HU').format(repetitionCount)} ${exercise.unit || ''}`.trim());
  }
  const duration = formatExerciseDuration(exercise);
  if (duration) {
    parts.push(`${duration} időtartam`);
  }
  return parts.join(', ');
};

export const getPublicPlayerName = (item = {}) => {
  return item.userNickname || item.user_nickname || item.userDisplayName || item.user_display_name || item.userEmail || item.user_email || 'Ismeretlen játékos';
};

export const normalizeExercise = (exerciseDoc) => {
  const data = exerciseDoc.data();
  return {
    id: exerciseDoc.id,
    name: typeof data.name === 'string' ? data.name : '',
    description: typeof data.description === 'string' ? data.description : '',
    repetitionCount: typeof data.repetition_count === 'number' ? data.repetition_count : 0,
    unit: typeof data.unit === 'string' ? data.unit : '',
    durationMinutes: typeof data.duration_minutes === 'number' ? data.duration_minutes : 0,
    durationSeconds: typeof data.duration_seconds === 'number' ? data.duration_seconds : 0,
    resultUnit: typeof data.result_unit === 'string' ? data.result_unit : '',
    resultDirection: normalizeResultDirection(data.result_direction),
    active: data.active !== false,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
};

export const normalizeFitnessResult = (resultDoc) => {
  const data = resultDoc.data();
  return {
    id: resultDoc.id,
    userId: typeof data.user_id === 'string' ? data.user_id : '',
    userEmail: typeof data.user_email === 'string' ? data.user_email : '',
    userDisplayName: typeof data.user_display_name === 'string' ? data.user_display_name : '',
    userNickname: typeof data.user_nickname === 'string' ? data.user_nickname : '',
    exerciseId: typeof data.exercise_id === 'string' ? data.exercise_id : '',
    exerciseName: typeof data.exercise_name === 'string' ? data.exercise_name : '',
    resultUnit: typeof data.result_unit === 'string' ? data.result_unit : '',
    resultDirection: normalizeResultDirection(data.result_direction),
    trainingDate: typeof data.training_date === 'string' ? data.training_date : '',
    trainingId: typeof data.training_id === 'string' ? data.training_id : '',
    resultValue: typeof data.result_value === 'number' ? data.result_value : Number(data.result_value),
    recordedAt: data.recorded_at || data.createdAt,
    recordedByUserId: typeof data.recorded_by_user_id === 'string' ? data.recorded_by_user_id : '',
    source: typeof data.source === 'string' ? data.source : 'user',
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
};

export const normalizePersonalRecord = (recordDoc) => {
  const data = recordDoc.data();
  return {
    id: recordDoc.id,
    userId: typeof data.user_id === 'string' ? data.user_id : '',
    userEmail: typeof data.user_email === 'string' ? data.user_email : '',
    userDisplayName: typeof data.user_display_name === 'string' ? data.user_display_name : '',
    userNickname: typeof data.user_nickname === 'string' ? data.user_nickname : '',
    exerciseId: typeof data.exercise_id === 'string' ? data.exercise_id : '',
    exerciseName: typeof data.exercise_name === 'string' ? data.exercise_name : '',
    resultUnit: typeof data.result_unit === 'string' ? data.result_unit : '',
    resultDirection: normalizeResultDirection(data.result_direction),
    resultValue: typeof data.result_value === 'number' ? data.result_value : Number(data.result_value),
    sortValue: typeof data.sort_value === 'number' ? data.sort_value : getResultSortValue(data.result_value, data.result_direction),
    achievedAt: data.achieved_at || data.createdAt,
    resultId: typeof data.result_id === 'string' ? data.result_id : '',
  };
};

export const buildBestResultsByUser = (results, direction = DEFAULT_RESULT_DIRECTION) => {
  const bestByUser = new Map();

  results.forEach((item) => {
    if (!item.userId || !Number.isFinite(Number(item.resultValue))) {
      return;
    }

    const current = bestByUser.get(item.userId);
    if (!current || isBetterResult(item, current, direction)) {
      bestByUser.set(item.userId, item);
    }
  });

  return Array.from(bestByUser.values());
};

export const sortRankingRows = (rows, direction = DEFAULT_RESULT_DIRECTION) => {
  return [...rows].sort((left, right) => {
    const leftValue = Number(left.resultValue ?? left.result_value);
    const rightValue = Number(right.resultValue ?? right.result_value);
    if (Number.isFinite(leftValue) && Number.isFinite(rightValue) && leftValue !== rightValue) {
      return normalizeResultDirection(direction) === 'lower'
        ? leftValue - rightValue
        : rightValue - leftValue;
    }
    return getTimestampMillis(left.achievedAt ?? left.recordedAt ?? left.recorded_at ?? left.createdAt)
      - getTimestampMillis(right.achievedAt ?? right.recordedAt ?? right.recorded_at ?? right.createdAt);
  });
};
